import React from "react";
import {
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import {
  ChartContainer,
  CustomTooltipContainer,
  TooltipTitle,
  TooltipValue,
  TooltipImage,
  TooltipContent,
} from "./index.styled";
import unionImage from "../../../../assets/images/union.webp";

interface PieChartData {
  source: string;
  views: number;
  addedUsers: number;
  conversionRate: number;
  subscriptionPercentage: number;
  color: string;
}

interface PieChartProps {
  data: PieChartData[];
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ value: number; name: string }>;
  coordinate?: { x: number; y: number };
}

const CustomTooltip = ({ active, payload, coordinate }: CustomTooltipProps) => {
  if (active && payload && payload.length) {
    const data = payload[0];
    const value = data.value;

    // Don't show tooltip if value is 0
    if (value === 0) {
      return null;
    }

    // Format number without any decimal places
    const formatValue = (num: number) => {
      if (num === 0) return "0";
      // Always round to whole number
      return Math.round(num).toString();
    };

    const formattedValue = formatValue(value);

    return (
      <CustomTooltipContainer $x={coordinate?.x || 0} $y={coordinate?.y || 0}>
        <TooltipImage src={unionImage} alt="tooltip" />
        <TooltipContent>
          <TooltipTitle>{data.name}</TooltipTitle>
          <TooltipValue>{formattedValue}%</TooltipValue>
        </TooltipContent>
      </CustomTooltipContainer>
    );
  }
  return null;
};

const PieChart: React.FC<PieChartProps> = ({ data }) => {
  const chartData = data.map((item) => ({
    name: item.source,
    value: item.subscriptionPercentage,
    color: item.color,
  }));

  return (
    <ChartContainer>
      <ResponsiveContainer width="100%" height="100%">
        <RechartsPieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            outerRadius={75}
            fill="#8884d8"
            dataKey="value"
            stroke="none"
            strokeWidth={0}
            animationBegin={0}
            animationDuration={1500}
            animationEasing="linear"
            startAngle={90}
            endAngle={450}
          >
            {chartData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={entry.color}
                style={{
                  animationDelay: `${index * 0.1}s`,
                  transition: "all 0.3s ease",
                }}
              />
            ))}
          </Pie>
          <Tooltip
            content={<CustomTooltip />}
            cursor={false}
            isAnimationActive={true}
            offset={0}
            allowEscapeViewBox={{ x: true, y: true }}
            animationDuration={300}
            animationEasing="ease-out"
          />
        </RechartsPieChart>
      </ResponsiveContainer>
    </ChartContainer>
  );
};

export default PieChart;
